import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Sparkles, Check } from 'lucide-react';
import { WordsPullUpMultiStyle } from '../components/AnimationComponents';

export const ProductsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const products = [
    "Floor Cleaner", "Phenyl", "Hand Wash", "Multi Surface Cleaner",
    "Mop", "Glass Cleaner", "Gloves", "Mask", "Sanitary Pad",
    "Dustbin", "Sanitizer", "Facial Tissue", "Urinal Seat",
    "Garbage Bag", "Cleaning Brush", "Flushmatic Balls",
    "Air Freshener", "Toilet Cleaner"
  ];

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="products" className="min-h-screen py-12 md:py-20 px-4 md:px-8" data-testid="products-section">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 mb-6"
          >
            <Sparkles className="w-8 h-8 text-[#90EE90] drop-shadow-lg" />
            <span className="text-white font-bold text-sm drop-shadow-lg tracking-widest">OUR PRODUCT RANGE</span>
            <Sparkles className="w-8 h-8 text-[#90EE90] drop-shadow-lg" />
          </motion.div>

          <WordsPullUpMultiStyle
            segments={[
              { text: "Wide Range of", className: "text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-lg" },
              { text: "Hygiene Solutions", className: "text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-normal text-[#87CEEB] drop-shadow-lg" }
            ]}
            containerClassName="mb-6"
          />

          <p className="text-white/90 text-sm md:text-base max-w-3xl mx-auto leading-relaxed drop-shadow">
            We offer a comprehensive range of hygiene products designed to meet the needs of schools, institutions, workplaces, and communities. Every product is selected with care to ensure quality, affordability, and effectiveness.
          </p>
        </div>

        {/* Products Grid */}
        <div ref={ref} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {products.map((product, i) => (
            <motion.div
              key={i}
              initial={{ y: 20, opacity: 0 }}
              animate={isInView ? { y: 0, opacity: 1 } : {}}
              transition={{
                duration: 0.5,
                delay: i * 0.05,
                ease: [0.16, 1, 0.3, 1]
              }}
              className="bg-white/15 backdrop-blur-md border border-white/30 rounded-xl p-6 hover:bg-white/25 hover:-translate-y-1 transition-all duration-300 shadow-lg"
              data-testid={`product-card-${i}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#90EE90]/30 flex items-center justify-center">
                  <Check className="w-4 h-4 text-[#90EE90]" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-base drop-shadow">
                    {product}
                  </h3>
                  <p className="text-white/70 text-xs mt-1">
                    Quality assured
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-12 md:mt-16 text-center"
        >
          <p className="text-white/90 text-base md:text-lg mb-6 max-w-2xl mx-auto drop-shadow">
            All our products are carefully selected to deliver quality, reliability, and affordability. We're committed to making hygiene accessible to everyone.
          </p>
          
          <motion.a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              scrollToContact();
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block px-8 py-4 bg-[#87CEEB] text-white rounded-full font-semibold text-base hover:bg-[#6BB6D9] transition-colors shadow-xl cursor-pointer"
            data-testid="bulk-orders-btn"
          >
            Get in Touch for Bulk Orders
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};
